import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { ProviderTypeFormRule, ProviderTypeFormSchema } from "@factory/form/form.rule";
import { useFormDefaultProviderType } from "@factory/form/form-default-provider-type.hook";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { 
  useQyUpdateProviderType, 
  useQyGetProviderTypeById, 
  useQyDeleteProviderType 
} from "@factory/query/provider-type.query";
import { GetResponseProviderTypeModel } from "@factory/model/query-model-provider-type";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";

export function useEditProviderType() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultProviderType();
  const navigate = useNavigate();
  const { mapUpdateProviderType, mapDeleteProviderType } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { providerTypeId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID ProviderType
  const handleGetApiSuccess = (data: GetResponseProviderTypeModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
      setValue("tenant", responseData?.tenant || "");
  };
  const {
    data: providerTypeResponse,
    isLoading,
    isError: providerTypeError,
  } = useQyGetProviderTypeById(providerTypeId || "", handleGetApiSuccess);
  const providerTypeData = providerTypeResponse?.data?.[0];

  // API UPDATE ProviderType
  const handleUpdateApiSuccess = () => {
    showSuccess("ProviderType updated successfully");
  };
  const { mutate: updateProviderType, isLoading: isEditLoading } = useQyUpdateProviderType(
    handleUpdateApiSuccess
  );

  // API DELETE ProviderType
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("ProviderType deleted successfully");
  };
  const { mutate: deleteProviderType } = useQyDeleteProviderType(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<ProviderTypeFormSchema>({
    defaultValues: getDefault(providerTypeData),
    resolver: zodResolver(ProviderTypeFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: ProviderTypeFormSchema) => {
    if (!providerTypeData || !providerTypeData.code) {
      throw new Error("No providerType data");
    }

    const formData = mapUpdateProviderType(form, providerTypeData.code);
    updateProviderType(formData);
  };
  const handleValidateError = (err: FieldErrors<ProviderTypeFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!providerTypeData || !providerTypeData.code) {
      throw new Error("No providerType data found");
    }

    const form = mapDeleteProviderType(providerTypeData.code);
    deleteProviderType(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    providerTypeId,
    providerTypeData,
    isLoading,
    providerTypeError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}